// Strings.js
/**
 * Vowels
 */
export const vowels = ['A', 'E', 'I', 'O', 'U']; // Array of vowel characters

/**
 * Truncates the file name
 */
export const truncateFileName = (fileName, maxLength) => {
  if (!fileName) return '';
  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.split('.').pop();
  const nameWithoutExtension = fileName.slice(0, fileName.length - extension.length - 1);

  const truncatedName = nameWithoutExtension.slice(0, maxLength - extension.length - 4); // 3 for '...' and 1 for the dot before extension

  return `${truncatedName}...${extension}`;
};

/**
 * Truncates the text and appends an ellipsis (...) if it exceeds the maxLength.
 * 
 * @param {string} text - The text to truncate.
 * @param {number} maxLength - The maximum allowed length of the text, including ellipsis.
 * @returns {string} - The truncated text with ellipsis if needed.
 */
export const truncateText = (text, maxLength) => {
  // If the text is already shorter than or equal to maxLength, return it as is.
  if (text.length <= maxLength) return text;

  // Subtract 3 for the ellipsis and return the truncated text with '...'
  return text.slice(0, maxLength - 3) + '...';
};

/**
 * Cleans a string by converting it to lowercase, removing spaces, and keeping only letters and numbers.
 * @param {string} input The input string to clean.
 * @returns {string} The cleaned string.
 */
export function cleanString(input) {
  return input.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * generateSubstrings
 * 
 * Generates substrings of a given string up to a specified maximum length,
 * useful for search indexing.
 * 
 * @param {string} text - The input string to generate substrings from.
 * @param {number} maxLength - The maximum length of substrings to generate.
 * 
 * @returns {array} - An array of generated substrings.
 */
export function generateSubstrings(text, maxLength = 5) {
    const substrings = new Set(); // Use Set to avoid duplicates
    const lowerText = text.toLowerCase();

    for (let length = 1; length <= Math.min(maxLength, lowerText.length); length++) {
        for (let start = 0; start <= lowerText.length - length; start++) {
            substrings.add(lowerText.slice(start, start + length));
        }
    }

    return Array.from(substrings);
}

/**
 * pluralize
 * 
 * Returns the plural form of a word based on the provided count.
 * 
 * @param {string} word - The word to pluralize.
 * @param {number} count - The count determining singular or plural form.
 * 
 * @returns {string} - The pluralized form of the word if count is not 1, otherwise the original word.
 */
export function pluralize(word, count) {
  if (count === 1) return word;
  const lower = word.toLowerCase();
  if (lower.endsWith('y') && !lower.endsWith('ay') && !lower.endsWith('ey') && !lower.endsWith('iy') && !lower.endsWith('oy') && !lower.endsWith('uy')) {
    return `${word.slice(0, -1)}ies`;
  }
  if (lower.endsWith('s') || lower.endsWith('x') || lower.endsWith('z') || lower.endsWith('ch') || lower.endsWith('sh')) {
    return `${word}es`;
  }
  return `${word}s`;
}

/**
 * getArticle
 * 
 * Returns the appropriate indefinite article ('a' or 'an') for a given word.
 * 
 * @param {string} word - The word to determine the article for.
 * 
 * @returns {string} - 'an' if the word starts with a vowel sound, otherwise 'a'.
 */
export const getArticle = (word) => {
  if (!word) return '';
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
};

/**
 * toFolderSlug
 *
 * Deterministic folder slug from a title.
 *
 * @param {string} value - Folder title.
 * @returns {string} - Slug string.
 */
export const toFolderSlug = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * toUpperSnakeCase
 *
 * Converts free text into UPPER_SNAKE_CASE.
 *
 * @param {string} value - Input text.
 * @returns {string} - Normalized upper snake case text.
 */
export const toUpperSnakeCase = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .toUpperCase();

/**
 * toLowerSnakeCase
 *
 * Converts free text into lower_snake_case.
 *
 * @param {string} value - Input text.
 * @returns {string} - Normalized lower snake case text.
 */
export const toLowerSnakeCase = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .toLowerCase();
